// src/hooks/useUserNavigation.js
import { useState, useEffect, useCallback, useRef } from "react";
import { getBestAvailableCurrentPosition } from "../utils/geo";
import {
  clearCachedGeoGrant,
  hasCachedGeoGrant,
  rememberGeoGrant,
} from "../utils/geoPermissionStorage";

export default function useUserNavigation(mapRef) {
  const cachedGeoGrantRef = useRef(hasCachedGeoGrant());
  const [userLocation, setUserLocation] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [geoError, setGeoError] = useState(null);

  // Permissions state (queried, not requested)
  const [permissionState, setPermissionState] = useState(() =>
    cachedGeoGrantRef.current ? "granted" : "unknown"
  );
  // 'prompt' | 'granted' | 'denied' | 'unsupported' | 'unknown'

  const [followMode, setFollowMode] = useState(false);
  const [directions, setDirections] = useState(null);
  const [travelTime, setTravelTime] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);

  // store the active navigation target
  const [destination, setDestination] = useState(null);
  const watchIdRef = useRef(null);
  const didRequestGrantedLocationRef = useRef(false);

  // --- NEW: Only *query* permission on mount; do not request location yet.
  useEffect(() => {
    let cleanup;
    const canQuery =
      typeof navigator !== "undefined" &&
      navigator.permissions &&
      navigator.permissions.query;

    if (canQuery) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((status) => {
          setPermissionState(status.state); // 'granted' | 'denied' | 'prompt'
          // Keep permissionState in sync if it changes while app is open
          const handler = () => setPermissionState(status.state);
          status.addEventListener?.("change", handler);
          cleanup = () => status.removeEventListener?.("change", handler);
        })
        .catch(() => {
          setPermissionState("unsupported");
        });
    } else {
      setPermissionState("unsupported");
    }
    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  // --- NEW: callable that actually requests the user's location (triggers the browser prompt)
  const requestInitialLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation not supported");
      setPermissionState("unsupported");
      return;
    }
    setIsLocating(true);
    setGeoError(null);

    getBestAvailableCurrentPosition()
      .then((pos) => {
        setUserLocation(pos);
        setIsLocating(false);
        setPermissionState("granted");
        cachedGeoGrantRef.current = true;
        rememberGeoGrant();
      })
      .catch((err) => {
        setIsLocating(false);
        setGeoError(err?.message || "Unable to get location");
        // 1 === PERMISSION_DENIED
        if (err?.code === 1) {
          setPermissionState("denied");
          cachedGeoGrantRef.current = false;
          clearCachedGeoGrant();
        }
      });
  }, []);

  useEffect(() => {
    const canUseCachedGrant =
      cachedGeoGrantRef.current &&
      (permissionState === "prompt" || permissionState === "unsupported");
    const shouldRequestInitialLocation =
      permissionState === "granted" || canUseCachedGrant;

    if (!shouldRequestInitialLocation) {
      didRequestGrantedLocationRef.current = false;
      return;
    }
    if (userLocation || isLocating || didRequestGrantedLocationRef.current) return;

    didRequestGrantedLocationRef.current = true;
    requestInitialLocation();
  }, [isLocating, permissionState, requestInitialLocation, userLocation]);

  // live‐follow when navigating, plus live‐recalculate
  useEffect(() => {
    if (isNavigating && navigator.geolocation && !watchIdRef.current) {
      watchIdRef.current = navigator.geolocation.watchPosition(
        ({ coords }) => {
          const pos = { lat: coords.latitude, lng: coords.longitude };
          setUserLocation(pos);

          if (mapRef.current) {
            mapRef.current.panTo(pos);
            if (coords.heading != null) {
              mapRef.current.setHeading(coords.heading);
            }
          }

          // if we have an active destination, recalc route & ETA
          if (destination) {
            const svc = new window.google.maps.DirectionsService();
            svc.route(
              {
                origin: pos,
                destination,
                travelMode: window.google.maps.TravelMode.WALKING,
              },
              (result, status) => {
                if (status === "OK" && result) {
                  setDirections(result);
                  setTravelTime(result.routes[0].legs[0].duration.text);
                } else {
                  console.error("Live route update failed:", status);
                }
              }
            );
          }
        },
        (err) => console.error("watchPosition error:", err),
        { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
      );
    } else if (!isNavigating && watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [isNavigating, mapRef, destination]);

  // recenter button logic—also clear out any active route
  const handleRecenter = useCallback(
    (onRecenterDone) => {
      if (!navigator.geolocation) {
        onRecenterDone?.(null);
        return;
      }
      getBestAvailableCurrentPosition()
        .then((pos) => {
          setUserLocation(pos);
          if (mapRef.current) {
            mapRef.current.panTo(pos);
            if (!directions) mapRef.current.setZoom(18);
            if (pos.heading != null) {
              mapRef.current.setHeading(pos.heading);
            }
          }
          setGeoError(null);
          setPermissionState("granted");
          cachedGeoGrantRef.current = true;
          rememberGeoGrant();
          setFollowMode(true);

          // CLEAR NAVIGATION
          setDirections(null);
          setTravelTime(null);
          setIsNavigating(false);
          setDestination(null);
          onRecenterDone?.(pos);
        })
        .catch((err) => {
          setGeoError(err?.message || "Unable to get location");
          if (err?.code === 1) {
            setPermissionState("denied");
            cachedGeoGrantRef.current = false;
            clearCachedGeoGrant();
          }
          onRecenterDone?.(null);
        });
    },
    [directions, mapRef]
  );

  // walking‐route calculator—now remembers destination
  const calculateRoute = useCallback(
    (dest, options = {}) => {
      const origin = options?.origin || userLocation;
      if (!origin || !dest) {
        return Promise.resolve({ ok: false, status: "MISSING_DATA" });
      }
      if (!window.google?.maps?.DirectionsService || !window.google?.maps?.TravelMode) {
        return Promise.resolve({ ok: false, status: "MAPS_NOT_READY" });
      }

      setDestination(dest);

      return new Promise((resolve) => {
        const svc = new window.google.maps.DirectionsService();
        svc.route(
          {
            origin,
            destination: dest,
            travelMode: window.google.maps.TravelMode.WALKING,
          },
          (result, status) => {
            if (status === "OK" && result) {
              setDirections(result);
              setTravelTime(result.routes[0].legs[0].duration.text);
              setIsNavigating(true);
              setFollowMode(true);
              resolve({ ok: true, status });
            } else {
              console.error("Directions failed:", status);
              resolve({ ok: false, status });
            }
          }
        );
      });
    },
    [userLocation]
  );

  // clear the active navigation
  const clearNavigation = useCallback(() => {
    setDirections(null);
    setTravelTime(null);
    setIsNavigating(false);
    setFollowMode(false);
    setDestination(null);
  }, []);

  return {
    userLocation,
    isLocating,
    geoError,

    permissionState,
    requestInitialLocation,

    followMode,
    directions,
    travelTime,
    destination,
    isNavigating,
    handleRecenter,
    calculateRoute,
    clearNavigation,
  };
}
